import type { ReactNode } from "react";
import { Collapse } from "@mui/material";
import cx from "classnames";
import ExpandControl from "src/components/general/ExpandControl/ExpandControl";
import { EXPAND_CONTROL_PROPS } from "./AiAssistantExpandableTree.constants";
import { getExpandableFrameClassName } from "./AiAssistantExpandableTree.utils";
import AiAssistantExpandableButton from "./components/AiAssistantExpandableButton/AiAssistantExpandableButton";
import AiAssistantExpandableTreeItem from "./components/AiAssistantExpandableTreeItem/AiAssistantExpandableTreeItem";

type AiAssistantExpandableTreeProps = {
  title: ReactNode;
  children: ReactNode;
  /** When omitted, preserves the collapsed default from EXPAND_CONTROL_PROPS. */
  defaultExpanded?: boolean;
  /** Controlled mode: when set, the tree follows it and toggles call `onExpandedChange`. */
  expanded?: boolean;
  onExpandedChange?: (next: boolean) => void;
  /** Frames the tree in a card while expanded (see getExpandableFrameClassName). */
  framed?: boolean;
  "data-test-id"?: string;
};

const AiAssistantExpandableTree = ({
  title,
  children,
  defaultExpanded,
  expanded,
  onExpandedChange,
  framed,
  "data-test-id": testId,
}: AiAssistantExpandableTreeProps) => (
  <ExpandControl
    {...EXPAND_CONTROL_PROPS}
    {...(defaultExpanded !== undefined ? { defaultExpanded } : {})}
    expanded={expanded}
    onChange={onExpandedChange}
  >
    {({ isExpanded, ToggleButton, toggleExpand }) => (
      <div className={framed ? getExpandableFrameClassName(isExpanded) : undefined}>
        <AiAssistantExpandableButton onClick={toggleExpand}>
          {ToggleButton}
          {title}
        </AiAssistantExpandableButton>
        {/* unmountOnExit avoids mounting heavy children (e.g. Prism syntax
            highlighter on multi-MB tool result bodies) when the tree is
            collapsed. Otherwise every streaming tick re-renders & re-tokenizes
            the hidden body and the tab freezes. */}
        <Collapse in={isExpanded} unmountOnExit>
          <ul
            className={cx(
              "flat-tree-list text-xs ml-1 pl-0! -translate-y-2 transition-all",
              {
                "opacity-0 -translate-y-6": !isExpanded,
              }
            )}
            data-test-id={testId}
          >
            <li className="gap-1 py-1 pl-6" />
            {children}
          </ul>
        </Collapse>
      </div>
    )}
  </ExpandControl>
);

AiAssistantExpandableTree.Item = AiAssistantExpandableTreeItem;

export default AiAssistantExpandableTree;
